import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';

import useCommonToast from '../../hooks/useCommonToast';

import { isSubmitDisabled } from '../../services/utils';
import { rescaleItem } from 'services/rescaling';

import InputFormikField from '../forms/InputFormikField';

// Shared component by Loads/Batteries/Renewables for adding new item of the given type
const RescaleItemModal = ({ itemTypeId, itemId, itemName, ...rest }) => {
    const intl = useIntl();
    const { toast } = useCommonToast();

    const rescalingSupportedItemTypes = useSelector((state) => state.item.rescalingSupportedItemTypes);
    const isRescalingSupported = rescalingSupportedItemTypes.some((item) => item.itemTypeId === itemTypeId);

    const initialFormValues = {
        scalingValue: 1,
    };

    const onFormSubmit = async ({ scalingValue }) => {
        const successMessage = intl.formatMessage({ id: 'rescaling_modal_success' });

        const displayToast = toast({
            message: successMessage,
            duration: 20000,
        });

        await rescaleItem(itemId, scalingValue).then(() => {
            rest.onClose();
            displayToast();
        });
    };

    const validateScalingValue = (value) => {
        if (value !== undefined && value !== '') {
            if (value <= 0) {
                return intl.formatMessage({ id: 'rescaling_scalingValue_input_error' });
            }
        } else {
            return intl.formatMessage({ id: 'rescaling_scalingValue_input_error' });
        }
    };

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <ModalContent pt={3}>
                <ModalHeader pr={12}>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="rescaling_modal_header" values={{ name: itemName }} />
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <Formik
                    enableReinitialize
                    initialValues={initialFormValues}
                    onSubmit={onFormSubmit}
                    validateOnChange={true}
                >
                    {({ isSubmitting, errors, touched }) => (
                        <Form>
                            <ModalBody pb={10} borderBottom="1px" borderColor="border-secondary">
                                <SimpleGrid mt={4} columns={{ base: 1, md: 2 }} spacingX={6} spacingY={4}>
                                    <InputFormikField
                                        id="scalingValue"
                                        name="scalingValue"
                                        type="number"
                                        label={<FormattedMessage id="rescaling_input_label" />}
                                        info={<FormattedMessage id="rescaling_input_tooltip" />}
                                        validate={validateScalingValue}
                                        isRequired
                                        isFastField
                                        step="any"
                                    />
                                </SimpleGrid>
                            </ModalBody>

                            <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                <Box ml="auto">
                                    <Button variant="secondary" onClick={rest.onClose}>
                                        <Box as="span" textTransform="capitalize">
                                            <FormattedMessage id="common_cancel" />
                                        </Box>
                                    </Button>

                                    <Button
                                        type="submit"
                                        isDisabled={!isRescalingSupported || isSubmitDisabled({ errors, touched })}
                                        isLoading={isSubmitting}
                                        ml={3}
                                    >
                                        <FormattedMessage id="rescaling_modal_button" />
                                    </Button>
                                </Box>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            </ModalContent>
        </Modal>
    );
};

export default RescaleItemModal;
